import { AUTH_COOKIE, readCookie } from '@artic/ui/lib/auth-cookies';
import { isAuthError, isAuthRetryableFetchError, type User } from '@supabase/supabase-js';

import { supabase } from './supabaseClient';

/**
 * Synchronous read of the current access token from the artic_token cookie.
 * The cookie is the single source of truth — it's written by api-client's
 * refresh path (`custom-fetch.ts`) and `App.tsx#syncCookies`, and read by
 * every authenticated microsite (hub, quality). Calling
 * `supabase.auth.getSession()` inline just to grab the token is what dragged
 * the refresh-retry surface into ~50 hand-written `api.ts` headers
 * (Sentry ARTIC-HUB-12X).
 */
export function getAuthToken(): string | null {
  return readCookie(AUTH_COOKIE) ?? null;
}

/**
 * Best-effort current-user fetch. Swallows transient supabase-auth-js
 * failures (429 rate-limit, network blip, refresh death) and returns null
 * instead of letting an unhandled promise rejection escape to
 * `onunhandledrejection`. Use for non-critical reads like Intercom identify
 * or timeline author lookups — anywhere a missing user is degraded UX
 * rather than a hard failure.
 */
export async function getCurrentUserSafe(): Promise<User | null> {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) return null;
    return data.user;
  } catch {
    return null;
  }
}

/**
 * True when an auth failure is a transport blip rather than a verdict on the
 * session: a failed fetch, a gateway error, a timeout or a rate limit. Callers
 * must never read `true` here as "signed out" — reading a blip that way is what
 * signed users out mid-session until App.tsx made its proactive `getUser()`
 * check DEV-only.
 *
 * Anything not positively identifiable as transient is treated as a genuine
 * failure — including `AuthUnknownError`, which carries no status — so an
 * unfamiliar shape stays visible rather than being downgraded to noise.
 */
export function isTransientAuthError(error: unknown): boolean {
  if (!isAuthError(error)) return false;
  // auth-js raises this for a failed fetch (status 0) and for every code in its
  // NETWORK_ERROR_CODES list (500-504, 520-530), which it documents as
  // "infrastructure errors [that] should not cause session invalidation". That
  // covers every 5xx GoTrue emits, so no explicit `>= 500` branch is needed.
  if (isAuthRetryableFetchError(error)) return true;
  // What auth-js does NOT class as retryable arrives as an AuthApiError. Of
  // those, only a timeout and a rate limit are a blip.
  return error.status === 408 || error.status === 429;
}
